import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { arrangeColumns } from '@user/components/xGrid'
import {
  selectUsersColumnOrder,
  useLoadListViewPreferencesOnce,
} from '@user/store/userPreferences'
import { selectAllRoles } from '@user/store/roles/slice'
import { Role } from '@user/store/roles/typings'
import { usersListColumns } from '@user/components/Settings/Users/UsersNew/usersColumn'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { ITenantUser } from '@user/store/users'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'

interface Props {
  handleEditClick: (userId: string, initialActive?: boolean) => void
  handleDeleteRequest: (userId: string) => void
  disableDeleteTooltip: (user: ITenantUser) => string | undefined
  updateUserStatus: (user: ITenantUser, reloadUsers?: boolean) => void
  handleUpdateFromPPRequest: (externalUserId: string) => void
  hasSSOErrors?: boolean
}

export const useGetColumnData = (props: Props) => {
  const {
    handleEditClick,
    handleDeleteRequest,
    disableDeleteTooltip,
    handleUpdateFromPPRequest,
    updateUserStatus,
    hasSSOErrors = false,
  } = props
  const userPrefsLoading = useLoadListViewPreferencesOnce()
  const roles: Role[] = useSelector(selectAllRoles)
  const apiRef = useGridApiRef()
  const tidalThemeActive = useTidalThemeActive()
  const { formatDate } = useLocalizationDateFormat()

  const columnOrder = useSelector(selectUsersColumnOrder)
  const [columns, columnVisibility] = useMemo(
    () =>
      arrangeColumns(
        columnOrder,
        usersListColumns({
          roles,
          handleEditClick,
          handleDeleteRequest,
          disableDeleteTooltip,
          handleUpdateFromPPRequest,
          updateUserStatus,
          tidalThemeActive,
          hasSSOErrors,
          formatDate,
        }),
        hasSSOErrors ? ['errors'] : [],
        [],
        hasSSOErrors,
      ),
    [
      columnOrder,
      roles,
      handleEditClick,
      handleDeleteRequest,
      disableDeleteTooltip,
      handleUpdateFromPPRequest,
      updateUserStatus,
      tidalThemeActive,
      hasSSOErrors,
      formatDate,
    ],
  )

  const loading = userPrefsLoading

  return { loading, columns, columnVisibility, apiRef }
}
